import { Box, styled, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { crownWhiteIconPath as crownIconPath, CrownIcon } from "utils/svgIcons";
import {
	Bonus,
	CrossOutLine,
	CrossOutValue,
	ImageWrap,
	RewardImage,
	StyledCard,
	Title
} from "pages/StoreFront/lib/OfferCard/styled";
import Button from "components/Buttons/ThemeButton";
import usePopUpsV2 from "components/PopUpManagerV2/hooks/usePopUpsV2";
import classnames from "classnames";
import { useAppDispatch, useAppSelector } from "hooks";
import {
	onDIPtrigger,
	selectDipState,
	selectPopUpManagerState,
	setPricePoint,
	setTriggerOnCache
} from "redux/slices";
import { IShopLoyaltyOffer } from "contexts/WebStoreProvider/types";
import { useWebStoreContext, useGoogleAnalyticsContext } from "contexts";
import { useDialogModalRedux } from "redux/slices/dialog/hooks/useDialogModalRedux";
import { WsDialogs } from "interfaces";
import { RootState } from "redux/store";
import { useSelector } from "react-redux";
import useRewardsById from "hooks/useRewardsById";

const LoyaltyOfferCard = ({
	className,
	data,
	backgroundImageUrl
}: {
	uniqueKey?: string;
	className?: string;
	data: IShopLoyaltyOffer;
	backgroundImageUrl: string;
}): JSX.Element => {
	const { authToken, userTracker, setActiveItemToBeClicked } =
		useWebStoreContext();
	const {
		syncUserTracker,
		setCurrentOffer,
		openLoginToContinueDialog,
		currentOffer,
		networkErrorDetected
	} = useWebStoreContext();

	const itemRef = useRef<HTMLDivElement>(null);

	const { variant } = useAppSelector(selectPopUpManagerState);
	const { stack } = usePopUpsV2();
	const { reportLoyaltyPurchaseEvent } = useGoogleAnalyticsContext();
	const { openDialog } = useDialogModalRedux();
	const [isCurrentOffer, setIsCurrentOffer] = useState<boolean>(false);
	const buyButtonRef = useRef(null);
	const dispatch = useAppDispatch();
	const showOfferId = useSelector(
		(state: RootState) => state.debug.showDebugLabels
	);
	const { data: dipsData } = useAppSelector(selectDipState);
	const {
		id: offerId,
		rewardId,
		bannerText,
		//description,
		loyaltyPointPrice: price,
		priority,
		sectionName,
		rewardImageUrl,
		endTime,
		campaignGroup,
		crossOutValue
	} = data;

	const displayRewards = useRewardsById(rewardId);

	const resetCurrentOffer = () => {
		setIsCurrentOffer(false);
		setCurrentOffer(null);
	};

	const getRewardDescription = () => {
		const reward = displayRewards?.[0];
		const value = Number(reward?.description?.replace(/[^0-9]/g, ""));
		const label = value > 1 ? reward?.pluralLabel : reward?.singularLabel;
		return `${value.toLocaleString("en-US")} ${label}`;
	};

	const onClose = () => {
		resetCurrentOffer();
		reportLoyaltyPurchaseEvent(
			"loyalty_purchase_cancelled",
			userTracker?.id as string,
			endTime,
			offerId,
			rewardId,
			priority,
			price,
			campaignGroup,
			sectionName
		);
		if (
			dipsData?.dips?.find(
				(obj) => obj.dip.triggerType === "purchaseCancelled-loyalty_points"
			)
		) {
			price && dispatch(setPricePoint(price));
			dispatch(setTriggerOnCache("purchaseCancelled-loyalty_points"));
		} else {
			dispatch(onDIPtrigger({ triggerType: "purchaseCancelled" }));
		}
	};

	const onClickConfirmBuy = async () => {
		if (
			variant === "tutorial-loyalty-shop" ||
			stack.some(
				({ variant: popupVariant }) =>
					popupVariant === "tutorial-loyalty-shop-v2"
			)
		) {
			return;
		}
		reportLoyaltyPurchaseEvent(
			"loyalty_purchase_started",
			userTracker?.id as string,
			endTime,
			offerId,
			rewardId,
			priority,
			price,
			campaignGroup,
			sectionName
		);
		setCurrentOffer(buyButtonRef.current);

		let latestUserTracker = userTracker;
		if (networkErrorDetected) {
			const userTrackerResponse = await syncUserTracker();
			if (userTrackerResponse) {
				latestUserTracker = userTrackerResponse.userTracker;
			}
		}

		if (!authToken?.value) {
			setActiveItemToBeClicked({
				type: "loyalty_offer",
				offerId,
				description: getRewardDescription(),
				price,
				rewardImageUrl,
				data,
				cbClose: onClose
			});
			openLoginToContinueDialog();
		} else if (
			// intercept insufficient points before making request
			typeof latestUserTracker?.loyaltyPoints !== "undefined" &&
			latestUserTracker.loyaltyPoints < price
		) {
			openDialog(WsDialogs.INSUFFICIENT_LOYALTY_POINT_BALANCE, {
				cbClose: resetCurrentOffer,
				NeededLoyaltyPoints: price,
				CurrentLoyaltyPoints: latestUserTracker?.loyaltyPoints
			});
		} else {
			openDialog(WsDialogs.LOYALTY_PURCHASE_CONFIRM, {
				offerId,
				itemDescription: getRewardDescription(),
				itemPrice: price,
				itemImageUrl: rewardImageUrl,
				data,
				cbClose: onClose
			});
		}
	};

	const StyledImageWrap = styled(ImageWrap)({
		backgroundImage: `url(${backgroundImageUrl})`,
		height: "185px"
	});

	useEffect(() => {
		if (currentOffer === buyButtonRef.current) {
			setIsCurrentOffer(true);
		} else {
			setIsCurrentOffer(false);
		}
	}, [currentOffer]);

	return (
		<StyledCard
			className={className}
			ref={itemRef}
			id={data.id}
			data-tutorial-loyalty-single-offer
		>
			<StyledImageWrap>
				<RewardImage src={rewardImageUrl} />
			</StyledImageWrap>
			{bannerText && <Bonus>{bannerText}</Bonus>}
			<div className="content">
				<Box>
					<Title>{getRewardDescription()}</Title>
					{showOfferId && (
						<Typography
							sx={{
								fontWeight: 700,
								color: "red",
								fontSize: "10px",
								height: "auto",
								textAlign: "center"
							}}
						>
							{offerId}
						</Typography>
					)}
				</Box>
				{/* {BUTTON BOX} */}
				<Box textAlign="center">
					<Box
						sx={{
							position: "relative",
							display: "flex",
							flexDirection: "column",
							justifyContent: "center",
							alignItems: "center",
							flexWrap: "wrap",
							whiteSpace: "nowrap",
							marginBottom: ".4rem"
						}}
					>
						<CrossOutValue style={{ fontSize: "18px" }}>
							{crossOutValue && (
								<>
									<CrossOutLine />
									<CrownIcon
										style={{
											height: "20px",
											width: "auto",
											marginRight: 2
										}}
									/>
									{Number(crossOutValue).toLocaleString("en-US")}
								</>
							)}
						</CrossOutValue>
					</Box>
					<Button
						ref={buyButtonRef}
						variant="contained"
						color="success"
						size="large"
						onClick={onClickConfirmBuy}
						sx={{
							width: "100%",
							paddingTop: "0.5rem",
							paddingBottom: "0.5rem",
							fontSize: "22px"
						}}
						className={classnames({
							isCurrentOffer,
							isCurrentOfferSet: !!currentOffer
						})}
					>
						<>
							<img
								src={crownIconPath}
								alt="crown-icon"
								style={{ marginRight: 3, width: "22px" }}
							/>
							{price.toLocaleString("en-US")}
						</>
					</Button>
				</Box>
			</div>
		</StyledCard>
	);
};

export default LoyaltyOfferCard;
